import { EOperationTabId, ORDER_ID_QUERY_PARAM, USER_ID_QUERY_PARAM } from "../constants";
import { isNullOrUndefined, isEnumValue, isDefined, isEmpty } from "./common";
import { ISessionFilterValues } from "../interfaces/session";

/**
 * Parse page number from URL search parameters
 * @param searchParams - URL search parameters
 * @param paramName - Name of the page parameter (default: 'page')
 * @returns Page number if valid, otherwise undefined
 */
export function parsePageFromUrl(
  searchParams: URLSearchParams,
  paramName = "page"
): number | undefined {
  const urlPage = searchParams.get(paramName);
  if (isNullOrUndefined(urlPage)) return undefined;

  const pageNumber = parseInt(urlPage, 10);
  if (isNaN(pageNumber) || pageNumber <= 0) {
    return undefined;
  }

  return pageNumber;
}

/**
 * Parse enum value from URL search parameters with validation
 * @param searchParams - URL search parameters
 * @param enumObj - Enum object to validate against
 * @param paramName - Name of the parameter to parse
 * @returns Enum value if valid, otherwise undefined
 */
export function parseEnumFromUrl<T extends Record<string, unknown>>(
  searchParams: URLSearchParams,
  enumObj: T,
  paramName: string
): T[keyof T] | undefined {
  const paramValue = searchParams.get(paramName);
  if (isNullOrUndefined(paramValue)) return undefined;

  if (isEnumValue(paramValue, enumObj)) {
    return paramValue;
  }

  return undefined;
}

/**
 * Get the session detail page link from a session ID
 * @param sessionId - The session ID
 * @returns The session detail page link, or "#" if sessionId is invalid
 */
export function getSessionLink(sessionId: string | null | undefined): string {
  if (!isDefined(sessionId) || isEmpty(sessionId)) {
    return "#";
  }
  return `/dashboard/sessions/${sessionId}`;
}

/**
 * Deep link to the Users page with the detail drawer open for a platform user.
 */
export function getUserLink(userId: string | null | undefined): string {
  if (isEmpty(userId)) {
    return "#";
  }
  return `/dashboard/users?${USER_ID_QUERY_PARAM}=${userId}`;
}

/**
 * Get the order detail page link from an order ID.
 * Order IDs are globally unique, so no userId is needed.
 * @param orderId - The order ID
 * @returns The order detail page link, or "#" if orderId is invalid
 */
export function getOrderLink(orderId: string | null | undefined): string {
  if (!isDefined(orderId) || isEmpty(orderId)) {
    return "#";
  }
  return `/dashboard/orders/${orderId}`;
}

/**
 * Get the orders list page link
 * @param userId - Optional user ID to filter orders
 * @returns The orders list page link, with userId query parameter if provided
 */
export function getOrdersListLink(userId?: string | null | undefined): string {
  const baseUrl = "/dashboard/orders";
  if (isDefined(userId) && !isEmpty(userId)) {
    return `${baseUrl}?${USER_ID_QUERY_PARAM}=${userId}`;
  }
  return baseUrl;
}

/**
 * Get the sessions list page filtered by subscription ID
 * @param subscriptionId - Subscription ID to filter sessions
 * @returns The sessions page link with subscription filter, or "#" if invalid
 */
export function getSessionsBySubscriptionLink(
  subscriptionId: string | null | undefined
): string {
  if (!isDefined(subscriptionId) || isEmpty(subscriptionId)) {
    return "#";
  }
  return `/dashboard/sessions?subscriptionId=${subscriptionId}`;
}

/**
 * Get the session assignment request detail page link from a request ID
 * @param requestId - The request ID
 * @returns The session assignment request detail page link, or "#" if requestId is invalid
 */
export function getSessionAssignmentRequestLink(
  requestId: string | null | undefined
): string {
  if (!isDefined(requestId) || isEmpty(requestId)) {
    return "#";
  }
  return `/dashboard/session-assignment-requests/${requestId}`;
}

/**
 * Get the session assignment requests page filtered by subscription ID
 * @param subscriptionId - Subscription ID to filter requests
 * @returns The session assignment requests page link, or "#" if invalid
 */
export function getSessionAssignmentRequestsLink(
  subscriptionId: string | null | undefined
): string {
  if (!isDefined(subscriptionId) || isEmpty(subscriptionId)) {
    return "#";
  }
  return `/dashboard/session-assignment-requests?subscriptionId=${subscriptionId}`;
}

/**
 * Get the sessions list page link
 * @param filters - Optional filter values to apply
 * @returns The sessions page link, with drawerFilters query parameter if filters are provided
 */
export function getSessionsLink(
  filters?: Partial<ISessionFilterValues>
): string {
  const baseUrl = "/dashboard/sessions";
  if (!isDefined(filters) || Object.keys(filters).length === 0) {
    return baseUrl;
  }
  const drawerFilters = JSON.stringify(filters);
  return `${baseUrl}?drawerFilters=${encodeURIComponent(drawerFilters)}`;
}

/**
 * Get the partner profile page link from a partner ID
 * @param partnerId - The partner ID
 * @returns The partner profile page link, or "#" if partnerId is invalid
 */
export function getPartnerLink(partnerId: string | null | undefined): string {
  if (!isDefined(partnerId) || isEmpty(partnerId)) {
    return "#";
  }
  return `/dashboard/partners/${partnerId}`;
}

/**
 * Get the members (home) page link
 * @returns The members page link
 */
export function getMembersLink(): string {
  return "/dashboard/members";
}

/**
 * Get the onboarding tasks page link with optional tab
 * @param tab - Optional onboarding tab ID
 * @returns The onboarding tasks page link, with tab query parameter if provided
 */
export function getOnboardingTasksLink(tab?: string): string {
  const baseUrl = "/dashboard/tasks/onboarding";
  if (isDefined(tab) && !isEmpty(tab)) {
    return `${baseUrl}?tab=${tab}`;
  }
  return baseUrl;
}

/**
 * Get the operations tasks page link with optional tab
 * @param tab - Optional operations tab ID
 * @returns The operations tasks page link, with tab query parameter if provided
 */
export function getOperationsTasksLink(tab?: EOperationTabId): string {
  const baseUrl = "/dashboard/tasks/operations";
  if (isDefined(tab) && !isEmpty(tab)) {
    return `${baseUrl}?tab=${tab}`;
  }
  return baseUrl;
}

export function getSubscriptionLink(
  subscriptionId: string | null | undefined
): string {
  if (!isDefined(subscriptionId) || isEmpty(subscriptionId)) {
    return "#";
  }
  return `/dashboard/subscriptions?subscriptionId=${subscriptionId}`;
}

/**
 * Get the subscriptions list page link
 * @param userId - Optional platform user ID to filter subscriptions
 */
export function getSubscriptionsListLink(userId?: string | null | undefined): string {
  const baseUrl = "/dashboard/subscriptions";
  if (isDefined(userId) && !isEmpty(userId)) {
    return `${baseUrl}?${USER_ID_QUERY_PARAM}=${userId}`;
  }
  return baseUrl;
}

/**
 * Get the subscriptions list page link filtered by order ID
 * @param orderId - Order ID to filter subscriptions
 */
export function getSubscriptionsByOrderLink(orderId: string | null | undefined): string {
  if (!isDefined(orderId) || isEmpty(orderId)) {
    return "#";
  }
  return `/dashboard/subscriptions?${ORDER_ID_QUERY_PARAM}=${orderId}`;
}

/**
 * Get the products list page link
 */
export function getProductsListLink(): string {
  return "/dashboard/products";
}

/**
 * Get the product detail drawer deep link from a product ID
 * @param productId - The product ID
 */
export function getProductLink(productId: string | null | undefined): string {
  if (!isDefined(productId) || isEmpty(productId)) {
    return "#";
  }
  return `/dashboard/products/${productId}`;
}
